import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MAILBOX_CAP_CEILING, signature, type MailboxResponse, type MailboxSettings } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";

/**
 * **Business → Customers** ([22](../../../../docs/22-the-mailbox-connection.md),
 * [ADR 0036](../../../../docs/adr/0036-a-letter-to-a-stranger-leaves-through-the-customers-own-mailbox-connected-with-one-sign-in-and-never-from-a-joogoos-domain.md)).
 *
 * Letters to people who have never heard of this business leave from **their own mailbox**, as them, and sit in
 * their own sent folder. Joogoos never writes to a stranger from a Joogoos address. So the whole ask on this page is
 * one button, and the page's job is to be honest about the four things that decide whether a letter arrives: which
 * address it comes from, who signs it, what the footer says, and how many may go today.
 *
 * What is deliberately not here: anything that could show the token. The API has no field to put it in.
 */

/** What the callback says when the browser comes back, in the words the page shows. */
const OUTCOMES: Record<string, { tone: "good" | "bad"; says: string }> = {
  connected: { tone: "good", says: "Your mailbox is connected. Letters will go from it." },
  cancelled: { tone: "bad", says: "Nothing was connected — the sign-in was cancelled." },
  expired: { tone: "bad", says: "That took too long, or was started somewhere else. Try again." },
  not_allowed: { tone: "bad", says: "Only an owner of this workspace can connect a mailbox." },
  no_send_scope: { tone: "bad", says: "Permission to send was not granted, so the mailbox could not be connected. It has been given back." },
  failed: { tone: "bad", says: "Your mail provider did not finish the connection. Nothing was changed." },
  unavailable: { tone: "bad", says: "This box cannot connect a mailbox." },
};

/** The one query key, so connecting, saving and disconnecting all redraw the same page. */
const KEY = ["mailbox"];

export function MailboxPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.mailbox() });
  /**
   * The word the callback redirected back with (`/?mailbox=connected`).
   *
   * Read once and taken out of the URL, so a reload does not say "connected" again about something that happened
   * ten minutes ago — the same rule the export and report links follow in `App.tsx`.
   */
  const [outcome, setOutcome] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const said = params.get("mailbox");
    if (!said) return;
    setOutcome(said);
    params.delete("mailbox");
    const rest = params.toString();
    history.replaceState(history.state, "", `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`);
    void qc.invalidateQueries({ queryKey: KEY });
  }, [qc]);

  const connect = useMutation({
    mutationFn: () => api.connectMailbox(),
    // The provider's own screen, not ours: this app hands the browser over and gets it back on the callback.
    onSuccess: (r) => { location.href = r.url; },
  });
  const save = useMutation({
    mutationFn: (s: MailboxSettings) => api.saveMailbox(s),
    onSuccess: (mailbox) => qc.setQueryData(KEY, (old: MailboxResponse | undefined) => old ? { ...old, mailbox } : old),
  });
  const disconnect = useMutation({
    mutationFn: () => api.disconnectMailbox(),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  /**
   * The business's own switch (22 §sending). Every letter's gate asks it, off until the owner says so, and nothing in
   * the app could turn it on: a letter approved and sent was refused with "sending is switched off for this
   * workspace" and nowhere to change it (2026-09-29). Customers reads it too, so both are asked again.
   */
  const letOut = useMutation({
    mutationFn: (on: boolean) => api.saveCustomerSettings({ sendingOn: on }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  const d = q.data;
  const mailbox = d?.mailbox ?? null;
  const said = outcome && Object.hasOwn(OUTCOMES, outcome) ? OUTCOMES[outcome] : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-lg font-semibold">Mailbox</h1>
        <span className="text-[12.5px] text-muted">letters to new customers go from your own address</span>
      </div>

      {said ? (
        <p className={`text-[13px] rounded-lg px-3 py-2 ${said.tone === "good" ? "bg-tint text-ink" : "border border-line-2 bg-panel text-ink-2"}`}>
          {said.says}
        </p>
      ) : null}

      <p className="text-[13px] leading-relaxed text-ink-2">
        A letter to someone who has never heard of you leaves through <b>your</b> mailbox, as you, and sits in your own
        sent folder. Your domain, your name, your reputation. Joogoos never writes to a stranger from a Joogoos
        address, so there is no domain to buy and no DNS to set up — the whole thing is one button.
      </p>

      {q.isPending ? <p className="text-sm text-faint">…</p> : null}
      {d && !d.available ? <p className="text-[13px] text-muted">{d.note ?? "Not available yet."}</p> : null}

      {mailbox ? (
        <Connected d={d!} save={save.mutate} saving={save.isPending} disconnect={disconnect.mutate} disconnecting={disconnect.isPending} reconnect={connect.mutate} reconnecting={connect.isPending} letOut={() => letOut.mutate(true)} lettingOut={letOut.isPending} />
      ) : d?.available ? (
        <div className="rounded-xl border border-line bg-panel p-4 flex flex-col gap-3">
          <p className="text-[13px] font-medium">Connect the mailbox you write from.</p>
          <p className="text-xs text-muted leading-relaxed">
            Google asks one thing: <i>send email on your behalf</i>. Joogoos does not read your mailbox,
            {d.repliesByAddress
              ? " so each letter carries a reply address of Joogoos's own: an answer stops the follow-ups and is sent on to your inbox."
              : " so it writes one letter to each company and no follow-ups, and an answer lands in your own inbox like any other."}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => connect.mutate()}
              disabled={!d.mayConnect || connect.isPending}
              className="px-3 py-2 rounded-lg bg-ink text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
            >
              <Icon name="send" size={14} /> Connect a Google mailbox
            </button>
            <span className="px-3 py-2 rounded-lg border border-line-2 text-sm text-faint">A Microsoft mailbox · later</span>
          </div>
          {/*
            * Said before the press, because it is a decision made once (the owner, 2026-10-21). Letters to strangers
            * are what a provider watches a mailbox for: a mailbox kept for them can be limited without touching the
            * everyday one, its answers are not buried, and whatever Joogoos is later allowed to read in it is only that.
            */}
          <p className="text-xs text-muted leading-relaxed" role="note">
            <b className="text-ink font-semibold">Our advice: use a mailbox kept for this.</b> One address only for
            letters to new customers, on a second domain of yours if you have one. If a provider ever limits it, your
            everyday email keeps working, and answers to your letters are easy to find.
          </p>
          {!d.mayConnect ? <p className="text-xs text-muted">Only an owner of this workspace can connect one.</p> : null}
          {connect.error ? <p className="text-xs text-bad">{String(connect.error)}</p> : null}
        </div>
      ) : null}

      {d ? <Gate gate={d.gate} sendingOn={d.sendingOn} /> : null}
      {d ? <Footer footer={d.footer} /> : null}
    </div>
  );
}

/** The mailbox as it stands, and the four things about it worth changing or knowing. */
function Connected({ d, save, saving, disconnect, disconnecting, reconnect, reconnecting, letOut, lettingOut }: {
  d: MailboxResponse;
  save: (s: MailboxSettings) => void;
  saving: boolean;
  disconnect: () => void;
  /** The same consent flow as a first connection: a second one for this workspace replaces the row (22). */
  reconnect: () => void;
  reconnecting: boolean;
  disconnecting: boolean;
  /** Turns the business's own sending switch on. Off again is Pause, which keeps everything else as it is. */
  letOut: () => void;
  lettingOut: boolean;
}) {
  const m = d.mailbox!;
  const [name, setName] = useState(m.signsAsName);
  const [role, setRole] = useState(m.signsAsRole);
  const [cap, setCap] = useState(String(m.dailyCap));
  const [confirming, setConfirming] = useState(false);
  const dirty = name !== m.signsAsName || role !== m.signsAsRole || cap !== String(m.dailyCap);
  const paused = m.status === "paused";

  return (
    <div className="rounded-xl border border-line bg-panel p-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${m.status === "connected" ? "bg-good" : m.status === "paused" ? "bg-line-2" : "bg-bad"}`} />
        <span className="text-sm font-semibold">Letters go from {m.displayName ? `${m.displayName} <${m.address}>` : m.address}</span>
        <span className="text-xs text-muted">
          {m.provider === "google" ? "Google" : "Microsoft"} · connected {new Date(m.connectedAt).toLocaleDateString()}
          {m.lastSentAt ? ` · last sent ${new Date(m.lastSentAt).toLocaleDateString()}` : " · nothing sent yet"}
        </span>
      </div>

      {m.status === "disconnected" ? (
        <div className="text-[13px] rounded-lg border border-line-2 px-3 py-2 flex flex-col gap-1.5">
          <span>Your mail provider stopped accepting our connection, so nothing can be sent. Connect it again.</span>
          {m.lastError ? <span className="text-xs text-muted">{m.lastError}</span> : null}
          {d.mayConnect ? (
            <button onClick={reconnect} disabled={reconnecting} className="self-start text-xs font-medium text-coral disabled:text-muted">Connect it again</button>
          ) : null}
        </div>
      ) : null}

      {/*
        * Since 2026-09-27 the connection asks to send and nothing else (ADR 0044, amended): Joogoos cannot read a
        * reply. Where this server has a reply address of its own (the fallback the owner chose on 2026-10-06), the
        * letters carry it and an answer still stops the follow-ups; where it has none, there are no follow-ups. Said
        * once, plainly, as how it works rather than as something missing — and either way a person can say so.
        */}
      {m.status !== "disconnected" && !m.canReadReplies ? (
        <p className="text-xs text-muted leading-relaxed">
          {d.repliesByAddress
            ? "Joogoos does not read this mailbox. Each letter carries a reply address of Joogoos's own, so an answer stops the follow-ups and is sent on to this mailbox."
            : "One letter to each company, and no follow-ups: Joogoos does not read this mailbox, so it would not know when somebody answered. Answers land in your own inbox, and They replied on the company in Customers says one came."}
        </p>
      ) : null}

      {m.free ? (
        <p className="text-xs text-muted leading-relaxed">
          This is a free mailbox. Its provider caps it low and a company's spam filter reads it as one, so an address
          on your own domain is worth having before you switch a sequence on. Ten letters a day from here will still go.
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Signs as</span>
          <input
            value={name} onChange={(e) => setName(e.target.value)} disabled={!d.mayConnect}
            className="px-3 py-2 rounded-lg border border-line-2 bg-page text-sm" placeholder="Your name"
          />
          <span className="text-xs text-faint">the name at the bottom of every letter — a real person who will answer</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Their role</span>
          <input
            value={role} onChange={(e) => setRole(e.target.value)} disabled={!d.mayConnect}
            className={`px-3 py-2 rounded-lg border bg-page text-sm ${role.trim() ? "border-line-2" : "border-coral"}`} placeholder="e.g. Owner"
          />
          {role.trim() ? null : (
            <span className="text-xs text-ink-2">Not set: letters are signed with your name alone. A role tells a stranger who is writing.</span>
          )}
        </label>
      </div>

      {/*
        * How a letter ends, drawn by the same function the letter is (R-3). Connecting proposes the account's name and
        * nothing else, so this is where a person sees the sign-off before a stranger does — and where a name the
        * account holds in lower case is offered back with capitals, never changed without asking.
        */}
      <div className="rounded-lg border border-line bg-page px-3 py-2.5 flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">How every letter ends</span>
        <p className="text-[13px] whitespace-pre-line">{signature({ signsAsName: name.trim() || m.signsAsName, signsAsRole: role, business: d.footer.company, website: d.footer.website })}</p>
        {d.mayConnect && capitalised(name) !== name ? (
          <button onClick={() => setName(capitalised(name))} className="self-start text-xs font-semibold text-coral">
            Sign as {capitalised(name)}
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1 min-w-[180px] flex-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted flex items-center gap-2">
            Today <span className="ml-auto tabular-nums text-faint">{m.sentToday} of {m.allowedToday}</span>
          </span>
          <span className="h-2 rounded-full bg-line-2 overflow-hidden">
            <span className="block h-full bg-ink" style={{ width: `${m.allowedToday ? Math.min(100, (m.sentToday / m.allowedToday) * 100) : 0}%` }} />
          </span>
          <span className="text-xs text-faint">
            {m.allowedToday < m.dailyCap
              ? "Warming up: five a day for the first week, so a new address is not judged on a burst."
              : "Your cap, and never more than a hundred whatever it says."}
          </span>
        </div>
        <label className="flex flex-col gap-1 w-[140px]">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Your cap</span>
          <input
            value={cap} onChange={(e) => setCap(e.target.value.replace(/[^\d]/g, ""))} disabled={!d.mayConnect}
            inputMode="numeric" className="px-3 py-2 rounded-lg border border-line-2 bg-page text-sm tabular-nums"
          />
          <span className="text-xs text-faint">a day, up to {MAILBOX_CAP_CEILING}</span>
        </label>
      </div>

      {d.mayConnect && !d.sendingOn ? (
        <div className="rounded-lg border border-line-2 bg-page px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-xs text-ink-2 leading-relaxed flex-1 min-w-0 basis-64">
            <b>Sending is off for this business.</b> Letters are written and wait for you; none leaves, even one you
            approve, until it is on.
          </span>
          <button onClick={letOut} disabled={lettingOut} className="px-3 py-2 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
            Turn sending on
          </button>
        </div>
      ) : null}

      {d.mayConnect ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => save({ signsAsName: name.trim() || m.signsAsName, signsAsRole: role.trim(), dailyCap: Number(cap || 0) })}
            disabled={!dirty || saving}
            className="px-3 py-2 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40"
          >
            Save
          </button>
          <button
            onClick={() => save({ paused: !paused })}
            disabled={saving || m.status === "disconnected"}
            className="px-3 py-2 rounded-lg border border-line-2 bg-panel text-xs font-medium disabled:opacity-40"
          >
            {paused ? "Start sending again" : "Pause sending"}
          </button>
          {confirming ? (
            <span className="flex items-center gap-2 text-xs">
              {/* Not at once when something else in Joogoos uses the same account: the provider keeps one grant for all of it (ADR 0059). */}
              <span className="text-muted">This deletes the key, and gives the permission back to your provider once nothing else in Joogoos uses this account.</span>
              <button onClick={() => disconnect()} disabled={disconnecting} className="px-2.5 py-1.5 rounded-lg bg-bad text-white font-semibold disabled:opacity-40">
                Disconnect
              </button>
              <button onClick={() => setConfirming(false)} className="px-2.5 py-1.5 rounded-lg border border-line-2">Keep it</button>
            </span>
          ) : (
            <button onClick={() => setConfirming(true)} className="ml-auto text-xs text-bad font-medium">Disconnect</button>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted">Only an owner of this workspace can change this.</p>
      )}
    </div>
  );
}

/**
 * What this box will actually let out.
 *
 * Said plainly, because a rehearsing box that looked like a live one is how somebody sends a real letter to a real
 * company by accident. The check itself is on the last line before the provider and no screen can move it.
 */
function Gate({ gate, sendingOn }: { gate: MailboxResponse["gate"]; sendingOn: boolean }) {
  const says = gate === "on"
    ? sendingOn ? "Letters go out to the companies you write to." : "This box can send, but this workspace's own switch is off."
    : gate === "rehearsal"
      ? "This box is rehearsing: every letter is delivered to our own address, whoever it is written to."
      : gate === "allowlist"
        ? "This box is rehearsing: letters only reach our own addresses, whoever they are written to."
        : "Nothing leaves this box. Letters can be written and nothing will be sent.";
  return (
    <p className="text-xs text-muted flex items-start gap-2">
      <Icon name="lock" size={13} className="mt-0.5 shrink-0" />
      <span>{says}</span>
    </p>
  );
}

/**
 * A name an account holds all in lower case, with a capital at the start of each part: `jean-christophe sassi` is
 * offered as `Jean-Christophe Sassi`. A name with any capital in it is left as its owner wrote it.
 */
export function capitalised(name: string): string {
  if (name !== name.toLowerCase()) return name;
  return name.replace(/(^|[\s-])(\p{L})/gu, (_, at: string, letter: string) => at + letter.toUpperCase());
}

/** The legal footer, read from the business page so there is exactly one place to correct it. */
function Footer({ footer }: { footer: MailboxResponse["footer"] }) {
  return (
    <div className="rounded-xl border border-line bg-panel p-4 flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">The footer under every letter</span>
      {footer.company || footer.address ? (
        <span className="text-[13px]">{[footer.company, footer.address].filter(Boolean).join(", ")}</span>
      ) : (
        <span className="text-[13px] text-muted">Your business page does not say the company's name and postal address yet.</span>
      )}
      <span className="text-xs text-faint">
        Read from your business page — change it there, not here. It has to be on a letter to a stranger, and it goes
        out under your name, so there is one place it comes from.
      </span>
    </div>
  );
}
